import React, { useCallback, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, xcss, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { HelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import FileCabinetOpen from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/file-cabinet-open/index.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	useChildIssuesLimit,
	useIsModal,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import { IN_PRODUCT_HELP } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import { getChildIssuesLimitExceededAnalyticsCount } from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';
import { TIME_TRACKING } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ChildIssuesPanelType } from '../../common/types.tsx';
import { useChildIssuesLimitHelp } from '../../services/use-child-issues-limit-help/index.tsx';
import { ButtonLinkWithShortcutIcon } from '../button-link-with-shortcut-icon/index.tsx';
import messages from './messages.tsx';
import { ViewInSearchButton } from './view-in-search-button/index.tsx';
import { BulkEditButton } from './bulk-edit-button/index.tsx';
import { TEST_ID, LEARN_MORE_LINK_TEST_ID } from './constants/index.tsx';

type Props = {
	hasExceededIssuesLimitInitialLoad: boolean;
	childIssuesPanelType: ChildIssuesPanelType;
	parentKey: IssueKey | null;
};

export const ChildIssuesLimitPanel = ({
	hasExceededIssuesLimitInitialLoad,
	childIssuesPanelType,
	parentKey,
}: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const isModal = useIsModal();
	const [{ value: fieldConfig }] = useFieldConfig(issueKey, TIME_TRACKING);

	const hasTimetracking = Boolean(fieldConfig);

	const childIssuesLimit = useChildIssuesLimit();
	const visibleIssuesCount = getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit);

	const onInProductHelpLinkClicked = useCallback(
		(event: MouseEvent, analyticsEvent: AnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'learnMoreAboutChildIssues', {
				issueCount: visibleIssuesCount,
			});
		},
		[visibleIssuesCount],
	);

	const childIssuesLimitHelp = useChildIssuesLimitHelp();

	const oldFallbackComponent = (
		<ButtonLinkWithShortcutIcon
			href={childIssuesLimitHelp.href}
			onClick={onInProductHelpLinkClicked}
			target="_blank"
			testId={LEARN_MORE_LINK_TEST_ID}
		>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.oldLearnMoreAboutChildIssuesLinkIssueTermRefresh
					: messages.oldLearnMoreAboutChildIssuesLink,
			)}
		</ButtonLinkWithShortcutIcon>
	);

	const newFallbackComponent = (
		<ButtonLinkWithShortcutIcon
			href={childIssuesLimitHelp.href}
			onClick={onInProductHelpLinkClicked}
			target="_blank"
			testId={LEARN_MORE_LINK_TEST_ID}
		>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.learnMoreAboutChildIssuesLinkIssueTermRefresh
					: messages.learnMoreAboutChildIssuesLink,
			)}
		</ButtonLinkWithShortcutIcon>
	);

	const fallbackComponent = fg('jiv-chin-child-issue-limit-panel-header-enablement')
		? newFallbackComponent
		: oldFallbackComponent;

	// Child Issue Limit Panel Title
	const childIssuesLimitTitle = fg('jiv-chin-child-issue-limit-panel-header-enablement')
		? messages.childIssuesLimitTitle
		: messages.oldChildIssuesLimitTitle;
	const childIssuesLimitTitleRefresh = fg('jiv-chin-child-issue-limit-panel-header-enablement')
		? messages.childIssuesLimitTitleIssueTermRefresh
		: messages.oldChildIssuesLimitTitleIssueTermRefresh;

	// Old  Description with Time Tracking
	const oldWithTimetrackingChildIssuesLimitDescriptionMessage = fg(
		'jira-issue-terminology-refresh-m3',
	)
		? messages.oldWithTimetrackingChildIssuesLimitDescriptionIssueTermRefresh
		: messages.oldWithTimetrackingChildIssuesLimitDescription;

	// Description with Time Tracking
	const withTimetrackingChildIssuesLimitDescriptionMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.withTimetrackingChildIssuesLimitDescriptionIssueTermRefresh
		: messages.withTimetrackingChildIssuesLimitDescription;

	// Old Description without Time Tracking
	const oldWithoutTimetrackingChildIssuesLimitDescriptionMessage = fg(
		'jira-issue-terminology-refresh-m3',
	)
		? messages.oldWithoutTimetrackingChildIssuesLimitDescriptionIssueTermRefresh
		: messages.oldWithoutTimetrackingChildIssuesLimitDescription;

	// Description without Time Tracking
	const withoutTimetrackingChildIssuesLimitDescriptionMessage = fg(
		'jira-issue-terminology-refresh-m3',
	)
		? messages.withoutTimetrackingChildIssuesLimitDescriptionIssueTermRefresh
		: messages.withoutTimetrackingChildIssuesLimitDescription;

	// Learn more about child issues link
	let oldLearnMoreAboutChildIssuesLinkMessage = messages.oldLearnMoreAboutChildIssuesLink;
	let learnMoreAboutChildIssuesLinkMessage = messages.learnMoreAboutChildIssuesLink;
	if (fg('jira-issue-terminology-refresh-m3')) {
		oldLearnMoreAboutChildIssuesLinkMessage =
			messages.oldLearnMoreAboutChildIssuesLinkIssueTermRefresh;
		learnMoreAboutChildIssuesLinkMessage = messages.learnMoreAboutChildIssuesLinkIssueTermRefresh;
	}

	return (
		<ChildrenLimitPanelContainer data-testid={TEST_ID}>
			<ImgContainer>
				<Box xcss={imageWrapperStyles}>
					<FileCabinetOpen alt="" />
				</Box>
			</ImgContainer>
			<DescriptionContainer>
				<Heading size="small">
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? childIssuesLimitTitleRefresh
							: childIssuesLimitTitle,
					)}
				</Heading>
				<LimitDescription>
					{fg('jiv-chin-child-issue-limit-panel-header-enablement') ? (
						<FormattedMessage
							{...(hasTimetracking
								? withTimetrackingChildIssuesLimitDescriptionMessage
								: withoutTimetrackingChildIssuesLimitDescriptionMessage)}
							values={{
								learnMoreLink:
									childIssuesLimitHelp.type === IN_PRODUCT_HELP && !isModal ? (
										<HelpPanelLink
											appearance="link"
											spacing="none"
											articleId={childIssuesLimitHelp.articleId}
											fallbackComponent={fallbackComponent}
											onClick={onInProductHelpLinkClicked}
											testId={LEARN_MORE_LINK_TEST_ID}
										>
											{formatMessage(learnMoreAboutChildIssuesLinkMessage)}
										</HelpPanelLink>
									) : (
										fallbackComponent
									),
								limit: childIssuesLimit,
							}}
						/>
					) : (
						<FormattedMessage
							{...(hasTimetracking
								? oldWithTimetrackingChildIssuesLimitDescriptionMessage
								: oldWithoutTimetrackingChildIssuesLimitDescriptionMessage)}
							values={{
								learnMoreLink:
									childIssuesLimitHelp.type === IN_PRODUCT_HELP && !isModal ? (
										<HelpPanelLink
											appearance="link"
											spacing="none"
											articleId={childIssuesLimitHelp.articleId}
											fallbackComponent={fallbackComponent}
											onClick={onInProductHelpLinkClicked}
											testId={LEARN_MORE_LINK_TEST_ID}
										>
											{formatMessage(oldLearnMoreAboutChildIssuesLinkMessage)}
										</HelpPanelLink>
									) : (
										fallbackComponent
									),
								limit: childIssuesLimit,
							}}
						/>
					)}
				</LimitDescription>
				<Inline space="space.100">
					<ViewInSearchButton
						hasExceededIssuesLimitInitialLoad={hasExceededIssuesLimitInitialLoad}
						childIssuesPanelType={childIssuesPanelType}
					/>
					{parentKey != null && fg('jiv-chin-child-issue-limit-panel-header-enablement') && (
						<BulkEditButton
							hasExceededIssuesLimitInitialLoad={hasExceededIssuesLimitInitialLoad}
							childIssuesPanelType={childIssuesPanelType}
							parentKey={parentKey}
						/>
					)}
				</Inline>
			</DescriptionContainer>
		</ChildrenLimitPanelContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChildrenLimitPanelContainer = styled.div({
	boxShadow: token('elevation.shadow.raised'),
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),

	backgroundColor: token('elevation.surface.raised'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${0.375 * gridSize}px`,
	marginTop: token('space.150'),
	marginBottom: token('space.100'),
});

const imageWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${6.125 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${8 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImgContainer = styled.div({
	flex: 1,
	marginLeft: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled.div({
	marginTop: token('space.200'),
	marginRight: token('space.200'),
	marginBottom: token('space.200'),
	marginLeft: token('space.200'),
	wordBreak: 'break-word',
	flex: 9,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LimitDescription = styled.p({
	fontFamily: token('font.family.body'),
	marginTop: token('space.100'),
	marginRight: '0px',
	marginBottom: token('space.100'),
	marginLeft: '0px',
});
